import './AddItemsForm.css';
import React, { useState } from 'react';
import { Order } from '../../../../../model/order';
import { Address } from '../../../../../model/company';
import { Tabs } from '../../../../../components/Tabs/Tabs';
import { ProductItemsForm } from './ProductItemsForm';
import { EventItemsForm } from './EventItemsForm/EventItemsForm';
import { Button } from '../../../../../components/Button/Button';
import { IncomingItemsForm } from './IncomingItemsForm';
import { Delivery, createDelivery } from '../../../../../model/delivery';
import { Invoice, createInvoice } from '../../../../../model/invoice';
import { handleError } from '../../../../../model/notification';
import { SimpleCheckbox } from '../../../../../components/Form/Checkbox/Checkbox';
import { t } from '../../../../../locales/translate';

interface Props {
  order: Order;
  preferedAddresses: {
    delivery: Address;
    invoice: Address;
  };
  onClose: () => void;
  onChange: () => void;
}

export function AddItemsForm({ order, preferedAddresses, onClose, onChange }: Props): JSX.Element {
  const [selectedTab, setSelectedTab] = useState<'product' | 'event' | 'incoming'>('product');

  return (
    <>
      <Tabs
        className="add-items-modal__header"
        inline
        tabGroups={[
          [
            {
              title: t.model.product.merch,
              active: selectedTab === 'product',
              onClick: () => setSelectedTab('product'),
            },
            {
              title: t.model.event._name,
              active: selectedTab === 'event',
              onClick: () => setSelectedTab('event'),
            },
            {
              title: t.model.order.incomingProducts,
              active: selectedTab === 'incoming',
              onClick: () => setSelectedTab('incoming'),
            },
          ],
        ]}
        useNavigation={false}
      />
      {selectedTab === 'product' && (
        <ProductItemsForm order={order} onClose={onClose} preferedAddresses={preferedAddresses} onChange={onChange} />
      )}
      {selectedTab === 'event' && (
        <EventItemsForm order={order} onClose={onClose} preferedAddresses={preferedAddresses} onChange={onChange} />
      )}
      {selectedTab === 'incoming' && (
        <IncomingItemsForm order={order} onClose={onClose} preferedAddresses={preferedAddresses} onChange={onChange} />
      )}
    </>
  );
}

export function AddItemsFormFooter({
  loading,
  canSubmit,
  createAnother,
  onChangeCreateAnother,
  onClose,
  handleSubmit,
}: {
  loading: boolean;
  canSubmit?: boolean;
  createAnother?: boolean;
  onChangeCreateAnother?: () => void;
  onClose: () => void;
  handleSubmit: () => Promise<void>;
}): JSX.Element {
  return (
    <footer className="add-items-modal__footer">
      <div className="add-items-modal__create-another">
        <SimpleCheckbox
          value={createAnother}
          onChange={onChangeCreateAnother}
          label={t.model.order.createAnother}
        ></SimpleCheckbox>
      </div>
      <div className="add-items-modal__close-save">
        <Button onClick={onClose} title={t.actions.close('')}></Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          disabled={!canSubmit}
          type="primary"
          title={t.actions.add('')}
        ></Button>
      </div>
    </footer>
  );
}

export async function createDocuments(options: {
  orderId: string;
  delivery?: boolean;
  invoice?: boolean;
  preferedAddresses: {
    delivery: Address;
    invoice: Address;
  };
}): Promise<[Delivery | undefined, Invoice | undefined]> {
  let createdDelivery: Delivery | undefined;
  let createdInvoice: Invoice | undefined;

  // Create documents
  if (options.delivery) {
    try {
      createdDelivery = await createDelivery(options.orderId, options.preferedAddresses.delivery);
    } catch (e) {
      handleError(e);
    }
  }

  if (options.invoice) {
    try {
      createdInvoice = await createInvoice(options.orderId, options.preferedAddresses.invoice);
    } catch (e) {
      handleError(e);
    }
  }

  return [createdDelivery, createdInvoice];
}

export function checkDocumentOption(option?: string | null): string | undefined | null {
  /* We use the 'no' value because we wan't to display that option in the selector
    and it cannot have the value undefined, because then we cannot tell if the option
    has actually been selected */
  if (option === 'no') return undefined;
  return option;
}
